import { useEffect, useRef, useCallback } from 'react'
import { useInterviewStore } from '@/store/interviewStore'
import { buildApiUrl, getApiHeaders } from '@/config/api'

interface DataModelingAutosaveOptions {
  intervalMs?: number
  content?: string
  entities: any[]
  relationships: any[]
  fkRelationships: any[]
  comments: any[]
  zoom: number
  panX: number
  panY: number
}

export function useDataModelingAutoSave({ 
  intervalMs = 5000, 
  content = '',
  entities = [], 
  relationships = [], 
  fkRelationships = [], 
  comments = [],
  zoom = 1,
  panX = 0,
  panY = 0
}: DataModelingAutosaveOptions) {
  const store = useInterviewStore()
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const lastSavedRef = useRef<string>('')
  const isSavingRef = useRef<boolean>(false)

  const saveToAPI = useCallback(async (data: any) => {
    if (isSavingRef.current) return
    
    const { interviewId } = store
    if (!interviewId) {
      console.warn('Cannot save data modeling: missing interviewId')
      return
    }

    try {
      isSavingRef.current = true
      
      const zoomPanState = {
        zoom: data.zoom || 1,
        panX: data.panX || 0,
        panY: data.panY || 0
      }

      const requestBody = {
        interviewId,
        content: data.content || '',
        save_type: 'auto',
        model_data: {
          content: data.content || '',
          entities: data.entities || [],
          relationships: data.relationships || [],
          fkRelationships: data.fkRelationships || [],
          comments: data.comments || [],
          zoom: zoomPanState.zoom,
          panX: zoomPanState.panX,
          panY: zoomPanState.panY
        },
        entities_data: data.entities || [],
        foreign_keys_data: data.fkRelationships || [],
        comments_data: data.comments || [],
        zoom_pan_state: zoomPanState
      }

      console.log('🔄 Auto-saving data modeling...')

      const response = await fetch(buildApiUrl('interview-responses/public/save/data_modeling'), {
        method: 'POST',
        headers: {
          ...getApiHeaders(false), // Public endpoint, no auth required
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      })

      if (!response.ok) {
        const error = await response.json()
        console.error('❌ Data modeling autosave failed:', error)
        return
      }

      // Mark as clean in store
      store.markStageClean('data-modeling')
      console.log('✅ Auto-saved data modeling')

    } catch (error) {
      console.error('❌ Data modeling autosave error:', error)
    } finally {
      isSavingRef.current = false
    }
  }, [store])

  const performSave = useCallback(() => {
    // Create current data snapshot
    const currentData = {
      content,
      entities,
      relationships,
      fkRelationships,
      comments,
      zoom,
      panX,
      panY
    }

    // Check if data has changed
    const dataString = JSON.stringify(currentData)
    const hasChanged = dataString !== lastSavedRef.current

    // Check if there's meaningful content
    const hasContent = Boolean(
      (content && content.trim().length > 0) ||
      entities.length > 0 || 
      relationships.length > 0 ||
      comments.length > 0 ||
      fkRelationships.length > 0
    )
    
    console.log('🔍 Data modeling checking changes:')
    console.log(`   - hasChanged: ${hasChanged}`)
    console.log(`   - hasContent: ${hasContent}`)
    console.log(`   - entities: ${entities.length}`)
    console.log(`   - comments: ${comments.length}`)
    console.log(`   - FK relationships: ${fkRelationships.length}`)
    
    // Only save if data has actually changed AND has meaningful content
    if (hasChanged && hasContent) {
      console.log('📝 Data modeling meaningful changes detected - triggering auto-save')
      saveToAPI(currentData)
      lastSavedRef.current = dataString
    } else {
      console.log('⏭️ Data modeling no meaningful changes - skipping API call')
    }
  }, [entities, relationships, fkRelationships, comments, zoom, panX, panY, saveToAPI])

  // Set up periodic auto-save
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    console.log('🟢 useDataModelingAutoSave: Starting periodic save')
    // Add a 3-second delay before starting auto-save to allow data loading to complete
    setTimeout(() => {
      intervalRef.current = setInterval(() => {
        console.log('⏰ useDataModelingAutoSave: interval triggered')
        performSave()
      }, intervalMs)
    }, 3000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
      console.log('🟢 useDataModelingAutoSave: Periodic save stopped')
    }
  }, [performSave, intervalMs])

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        console.log('🔄 Page hidden, triggering immediate data modeling save...')
        performSave()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [performSave])

  // Get current stage data for dirty state
  const stageData = store.getStageData('data-modeling')

  return {
    isDirty: Boolean(
      stageData.isDirty ||
      entities.length > 0 ||
      relationships.length > 0 ||
      comments.length > 0 ||
      fkRelationships.length > 0
    ),
    lastSaved: stageData.lastSaved,
    save: performSave
  }
}
